import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {Validators} from '@angular/forms';
import {ConfiguratorProductService} from '@app/product/configurator/services/configurator-product.service';
import {DcpProductDataService} from '@app/product/data/dcp-product-data.service';
import {ContractDurationService} from '@app/services/contract-duration-service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {ContractDuration, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {FieldType} from '@ngx-formly/core';
import {ActiveCartService} from '@spartacus/cart/base/core';
import {Product} from '@spartacus/core/src/model/product.model';
import {RadioGroupData} from 'ng-design-system';
import {Observable} from 'rxjs';
import {first, map} from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'dcp-contract-duration',
  templateUrl: './contract-duration.component.html',
  styleUrls: ['./contract-duration.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FormlyContractDurationComponent extends FieldType implements OnInit {

  contractDuration$: Observable<RadioGroupData[]>;

  static convertToRadioGroupData(contractDurations: ContractDuration[]): RadioGroupData[] {
    return contractDurations?.map(cd => ({
      value: JSON.stringify(cd),
      displayText: cd.value + ' ' + cd.metric?.name
    }));
  }

  constructor(
    private configuratorProductService: ConfiguratorProductService,
    private productDataService: DcpProductDataService,
    private activeCartService: ActiveCartService,
    private orderEntryService: DcpOrderEntryService,
    private contractDurationService: ContractDurationService,
    private jsonService: JsonService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.contractDuration$ = this.getContractDurations();
    this.contractDuration$.pipe(first(), untilDestroyed(this)).subscribe(duration => {
      if (duration?.length) {
        this.formControl.setValidators([Validators.required]);
      }
    });
  }

  /**
   * Gets the ContractDurations for the current VariantProduct by parsing the json data
   * or return [] when data is not set or ContractDuration is []
   *
   * @return Observable<ContractDurations[]> an Observable containing the ContractDurations as an array
   */
  private getContractDurations(): Observable<RadioGroupData[]> {
    return this.configuratorProductService.getVariant$().pipe(
      map(product => {
        const contractDurations: ContractDuration[] = this.productDataService.getContractDurations(product?.data);
        this.setCartOrDefaultValue(contractDurations, product);
        return FormlyContractDurationComponent.convertToRadioGroupData(contractDurations) || [];
      })
    );
  }

  /**
   * Sets the default value
   *
   * @param contractDurations list of all selectable ContractDurations
   */
  setDefaultValue(contractDurations: ContractDuration[]): void {
    const defaultContractDuration: ContractDuration = contractDurations?.find(c => c.default) || contractDurations[0];
    this.setFormValue(defaultContractDuration);
    this.emitSelectedDuration();
  }

  /**
   * Sets the default value
   *
   * @param contractDurations list of all selectable ContractDurations
   * @param product current Product to check if it's in the cart.
   */
  setCartOrDefaultValue(contractDurations: ContractDuration[], product: Product): void {
    if (contractDurations?.length && !this.formControl.value) {
      this.activeCartService.getEntry(product.code).pipe(untilDestroyed(this)).subscribe(
        cartEntry => {
          const cartDuration: ContractDuration = this.orderEntryService.getConfiguration(cartEntry)?.duration;

          if (cartEntry && cartDuration) {
            this.setFormValue(cartDuration);
          } else {
            this.setDefaultValue(contractDurations);
          }
        },
        () => {
          this.setDefaultValue(contractDurations);
        }
      );
    }
  }

  setFormValue(cd: ContractDuration): void {
    this.formControl.setValue(JSON.stringify(cd));
  }

  emitSelectedDuration() {
    if (this.formControl?.value) {
      this.contractDurationService.setContractDuration(this.jsonService.parseSafely(this.formControl.value));
    }
  }
}
